'use client'
import * as React from 'react'
import { ToolProgress } from '@/components/tools/tool-progress'
import { Search, Loader2, Sparkles, AlertTriangle, ExternalLink, Layers } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { detectStack, CATEGORY_LABELS, type StackResult, type DetectedApp } from '@/lib/stackDetect'

// Detection runs in the browser. The HTML is fetched through the
// in-house server proxy first (Edge, same-origin), then public CORS
// proxies as a fallback if the server proxy is blocked. We never log
// the URL or store any result.

async function fetchViaProxy(url: string): Promise<string> {
  const sources: Array<() => Promise<string>> = [
    async () => {
      const r = await fetch(`/api/proxy-fetch?url=${encodeURIComponent(url)}`, { signal: AbortSignal.timeout(15000) })
      const j = await r.json()
      if (!r.ok || typeof j.contents !== 'string') throw new Error(j.error || 'proxy-fetch failed')
      return j.contents
    },
    async () => {
      const r = await fetch(`https://corsproxy.io/?${encodeURIComponent(url)}`, { signal: AbortSignal.timeout(15000) })
      if (!r.ok) throw new Error('corsproxy.io returned ' + r.status)
      return await r.text()
    },
    async () => {
      const r = await fetch(`https://api.allorigins.win/get?url=${encodeURIComponent(url)}&nocache=${Date.now()}`, { signal: AbortSignal.timeout(15000) })
      if (!r.ok) throw new Error('allorigins returned ' + r.status)
      const j = await r.json()
      if (typeof j.contents !== 'string') throw new Error('allorigins envelope missing')
      return j.contents
    },
    async () => {
      const r = await fetch(`https://api.codetabs.com/v1/proxy/?quest=${encodeURIComponent(url)}`, { signal: AbortSignal.timeout(15000) })
      if (!r.ok) throw new Error('codetabs returned ' + r.status)
      return await r.text()
    },
  ]
  const errs: string[] = []
  for (const src of sources) {
    try {
      const html = await src()
      if (html && html.length > 100) return html
      errs.push('empty body')
    } catch (e) {
      errs.push(e instanceof Error ? e.message : String(e))
    }
  }
  throw new Error(`All proxies failed, ${errs.join(' · ')}`)
}

function normalizeUrl(input: string) {
  const trimmed = input.trim()
  if (!trimmed) return ''
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return 'https://' + trimmed.replace(/^\/+/, '')
}

// Many UGC / video widgets only mount on PDPs. Pull the first product
// link from the homepage and scan it too, then concatenate the HTML.
function extractFirstProductUrl(homepageUrl: string, html: string): string | null {
  const path = html.match(/href=["'](\/(?:products|product|shop|p)\/[a-z0-9][a-z0-9/-]*)(?:[?#"'][^"']*)?["']/i)?.[1]
  if (!path) return null
  try {
    const u = new URL(homepageUrl)
    return `${u.origin}${path}`
  } catch {
    return null
  }
}

function groupByCategory(apps: DetectedApp[]) {
  const map = new Map<string, DetectedApp[]>()
  for (const a of apps) {
    if (!map.has(a.category)) map.set(a.category, [])
    map.get(a.category)!.push(a)
  }
  return map
}

export function CompetitorStackDetector() {
  const [url, setUrl] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [result, setResult] = React.useState<StackResult | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setResult(null)
    const target = normalizeUrl(url)
    if (!target) {
      setError('Enter a store URL, for example glossier.com or https://allbirds.com.')
      return
    }
    setLoading(true)
    try {
      const homepage = await fetchViaProxy(target)
      let combined = homepage
      let pdpUrl: string | null = null
      try {
        pdpUrl = extractFirstProductUrl(target, homepage)
        if (pdpUrl) {
          const pdpHtml = await fetchViaProxy(pdpUrl)
          combined = homepage + '\n<!-- PDP -->\n' + pdpHtml
        }
      } catch {
        /* PDP fetch is best-effort; homepage-only detection still works */
      }
      const detection = detectStack(combined)
      if (pdpUrl) detection.signals.push(`Scanned homepage + a PDP (${new URL(pdpUrl).pathname}).`)
      setResult(detection)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch. The store may block scraping or every proxy timed out.')
    } finally {
      setLoading(false)
    }
  }

  const grouped = result ? groupByCategory(result.apps) : null

  return (
    <div className="rounded-2xl border bg-card shadow-sm">
      <form onSubmit={onSubmit} className="flex flex-col gap-3 border-b p-5 sm:flex-row sm:items-end sm:p-6">
        <label className="flex-1">
          <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">Store URL</span>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="glossier.com" disabled={loading} className="mt-1.5" />
        </label>
        <Button type="submit" disabled={loading} className="sm:w-48">
          {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Detecting</> : <><Search className="mr-2 h-4 w-4" /> Detect stack</>}
        </Button>
      </form>

      {loading && (
        <ToolProgress label="the site" steps={[{ title: "Fetching the site", sub: "Loading the page" }, { title: "Detecting the stack", sub: "UGC, reviews, platform and apps" }, { title: "Compiling the report", sub: "What they run, and the gaps" }]} />
      )}

      {error && (
        <div className="flex items-start gap-3 border-b bg-amber-50 px-5 py-4 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200 sm:px-6">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="grid gap-4 p-5 sm:p-6 md:grid-cols-2 md:gap-6">
          <div className="rounded-xl border p-4">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
              <Layers className="h-4 w-4" /> Platform
            </div>
            <div className="mt-1.5 font-display text-xl font-medium">{result.platformLabel}</div>
          </div>
          <div className={cn('rounded-xl border p-4', result.apps.length > 0 && 'border-primary/40 bg-primary/5')}>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
              <Sparkles className="h-4 w-4 text-primary" /> Social-proof stack
            </div>
            <div className="mt-1.5 font-display text-xl font-medium">
              {result.apps.length ? `${result.apps.length} app${result.apps.length === 1 ? '' : 's'} across ${result.categories.length} categor${result.categories.length === 1 ? 'y' : 'ies'}` : 'No UGC / reviews / video apps found'}
            </div>
          </div>

          {result.isIdukkiCustomer && (
            <div className="md:col-span-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-200">
              <strong className="font-medium">Idukki detected on this store.</strong>{' '}
              Already running Idukki. To get more from it, layer shoppable video onto the PDP, a hashtag wall on the homepage, and Klaviyo flows on UGC events.
            </div>
          )}

          {grouped && result.apps.length > 0 && (
            <div className="md:col-span-2 grid gap-3 sm:grid-cols-2">
              {result.categories.map((cat) => (
                <div key={cat} className="rounded-xl border bg-card p-4">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{CATEGORY_LABELS[cat]}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {grouped.get(cat)!.map((a) => (
                      <span key={a.name} className="rounded-full border bg-secondary px-2 py-0.5 text-xs">{a.name}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {result.apps.length === 0 && (
            <div className="md:col-span-2 rounded-xl border bg-primary/5 p-4 text-sm text-primary">
              <strong className="font-medium">Idukki tip:</strong> a clean install is the easiest time to consolidate. One platform for UGC galleries, shoppable video and reviews beats stitching three vendors together.
            </div>
          )}

          {result.signals.length > 0 && (
            <div className="md:col-span-2 rounded-xl border bg-card p-4">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Signals</div>
              <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                {result.signals.map((s) => <li key={s}>· {s}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-3 border-t bg-secondary/30 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <p className="text-xs text-muted-foreground sm:text-sm">
          Fetched server-side or through a CORS proxy. We don&rsquo;t log URLs or store results. Detection reads only the served HTML.
        </p>
        <Button asChild size="sm" variant="outline">
          <a href="https://apps.shopify.com/idukki-no-code-ugc-platform" target="_blank" rel="noopener noreferrer">
            Idukki on Shopify <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
          </a>
        </Button>
      </div>
    </div>
  )
}
